import { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import {
  createEditor,
  Descendant,
  Editor,
  Element as SlateElement,
  Transforms,
} from 'slate';
import {
  Editable,
  RenderElementProps,
  RenderLeafProps,
  Slate,
  useSlate,
  withReact,
} from 'slate-react';

import {
  ActionIcon,
  Button,
  Code,
  Divider,
  Group,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconBold,
  IconH1,
  IconH2,
  IconH3,
  IconH4,
  IconH5,
  IconH6,
  IconItalic,
  IconLink,
  IconList,
  IconListNumbers,
  IconUnderline,
} from '@tabler/icons-react';

import {
  AlignTypes,
  BlockTypes,
  CustomText,
  LIST_TYPES,
  MarkTypes,
} from '../interfaces/slate.interface';

// Block
// --------------------------------------------------------------------------
const isBlockActive = (editor: Editor, format: BlockTypes) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) && SlateElement.isElement(n) && n.type === format,
    }),
  );

  return !!match;
};

const toggleBlock = (editor: Editor, format: BlockTypes) => {
  const isActive = isBlockActive(editor, format);

  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes<SlateElement>(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type),
    split: true,
  });

  const newProperties: Partial<SlateElement> = {
    type: isActive ? 'paragraph' : isList ? 'list-item' : format,
  };

  Transforms.setNodes<SlateElement>(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

const BlockButton = ({
  format,
  icon,
  label,
}: {
  format: BlockTypes;
  icon: ReactNode;
  label: string;
}) => {
  const editor = useSlate();

  return (
    <Tooltip label={label}>
      <ActionIcon
        variant={isBlockActive(editor, format) ? 'filled' : 'light'}
        onClick={(event) => {
          event.preventDefault();
          toggleBlock(editor, format);
        }}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
};

// Align
// --------------------------------------------------------------------------
const isAlignActive = (editor: Editor, align: AlignTypes) => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: (n) =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (n as any).align === align,
    }),
  );

  return !!match;
};

const toggleAlign = (editor: Editor, align: AlignTypes) => {
  const newProperties: Partial<SlateElement> = {
    align: isAlignActive(editor, align) ? undefined : align,
  };

  Transforms.setNodes<SlateElement>(editor, newProperties);
};

const AlignButton = ({
  align,
  icon,
  label,
}: {
  align: AlignTypes;
  icon: ReactNode;
  label: string;
}) => {
  const editor = useSlate();

  return (
    <Tooltip label={label}>
      <ActionIcon
        variant={isAlignActive(editor, align) ? 'filled' : 'light'}
        onClick={(event) => {
          event.preventDefault();
          toggleAlign(editor, align);
        }}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
};

// Mark
// --------------------------------------------------------------------------
const isMarkActive = (editor: Editor, mark: MarkTypes) => {
  const marks = Editor.marks(editor);
  const markValue = marks?.[mark as keyof Omit<CustomText, 'text'>];

  return markValue !== false && markValue !== undefined;
};

const toggleMark = (editor: Editor, mark: MarkTypes, value: unknown = true) => {
  const isActive = isMarkActive(editor, mark);

  if (isActive) {
    Editor.removeMark(editor, mark);
  } else {
    Editor.addMark(editor, mark, value);
  }
};

const MarkButton = ({
  mark,
  icon,
  label,
  value = true,
}: {
  mark: MarkTypes;
  icon: ReactNode;
  label: string;
  value?: unknown;
}) => {
  const editor = useSlate();

  return (
    <Tooltip label={label}>
      <ActionIcon
        variant={isMarkActive(editor, mark) ? 'filled' : 'light'}
        onClick={(event) => {
          event.preventDefault();
          toggleMark(editor, mark, value);
        }}
      >
        {icon}
      </ActionIcon>
    </Tooltip>
  );
};

// Element and leaf render
// --------------------------------------------------------------------------
const Element = ({ attributes, children, element }: RenderElementProps) => {
  const style = {
    marginTop: '0',
    marginBottom: '0',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    textAlign: (element as any).align || 'left',
  };

  switch (element.type) {
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      );
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      );
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      );
    case 'heading-three':
      return (
        <h3 style={style} {...attributes}>
          {children}
        </h3>
      );
    case 'heading-four':
      return (
        <h4 style={style} {...attributes}>
          {children}
        </h4>
      );
    case 'heading-five':
      return (
        <h5 style={style} {...attributes}>
          {children}
        </h5>
      );
    case 'heading-six':
      return (
        <h6 style={style} {...attributes}>
          {children}
        </h6>
      );
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      );
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      );
    case 'paragraph':
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      );
  }
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
  if (leaf.link) {
    children = <a href={leaf.link}>{children}</a>;
  }

  if (leaf.bold) {
    children = <strong>{children}</strong>;
  }

  if (leaf.italic) {
    children = <em>{children}</em>;
  }

  if (leaf.underline) {
    children = <u>{children}</u>;
  }

  return <span {...attributes}>{children}</span>;
};

interface WYSIWYGProps {
  dynamicVariables?: string[];
  dynamicLinks?: { href: string; label: string }[];
  initialValue: Descendant[];
  onSave: (content: string, html: string) => void;
  isLoading?: boolean;
}

export default function WYSIWYG({
  initialValue,
  dynamicVariables,
  dynamicLinks,
  onSave,
  isLoading,
}: WYSIWYGProps) {
  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    [],
  );
  const renderLeaf = useCallback(
    (props: RenderLeafProps) => <Leaf {...props} />,
    [],
  );
  const editor = useMemo(() => withReact(createEditor()), []);

  const editorRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState<Descendant[]>(initialValue);

  const handleSaveClick = useCallback(() => {
    const html = editorRef.current?.innerHTML || '';

    onSave(JSON.stringify(value), html);
  }, [value, onSave]);

  return (
    <>
      <Slate editor={editor} initialValue={initialValue} onChange={setValue}>
        <Group mb="sm" gap="xs">
          <MarkButton mark="bold" icon={<IconBold />} label="Grassetto" />
          <MarkButton mark="italic" icon={<IconItalic />} label="Corsivo" />
          <MarkButton
            mark="underline"
            icon={<IconUnderline />}
            label="Sottolineato"
          />
          <Divider orientation="vertical" />
          <BlockButton
            format="heading-one"
            icon={<IconH1 />}
            label="Titolo 1"
          />
          <BlockButton
            format="heading-two"
            icon={<IconH2 />}
            label="Titolo 2"
          />
          <BlockButton
            format="heading-three"
            icon={<IconH3 />}
            label="Titolo 3"
          />
          <BlockButton
            format="heading-four"
            icon={<IconH4 />}
            label="Titolo 4"
          />
          <BlockButton
            format="heading-five"
            icon={<IconH5 />}
            label="Titolo 5"
          />
          <BlockButton
            format="heading-six"
            icon={<IconH6 />}
            label="Titolo 6"
          />
          <BlockButton
            format="numbered-list"
            icon={<IconListNumbers />}
            label="Lista numerata"
          />
          <BlockButton
            format="bulleted-list"
            icon={<IconList />}
            label="Lista puntata"
          />
          <Divider orientation="vertical" />
          <AlignButton
            align="left"
            icon={<IconAlignLeft />}
            label="Allinea a sinistra"
          />
          <AlignButton
            align="center"
            icon={<IconAlignCenter />}
            label="Allinea al centro"
          />
          <AlignButton
            align="right"
            icon={<IconAlignRight />}
            label="Allinea a destra"
          />
          <AlignButton
            align="justify"
            icon={<IconAlignJustified />}
            label="Allinea a destra"
          />
        </Group>
        {dynamicVariables && dynamicVariables.length > 0 && (
          <Group mb="sm">
            <Text fw="bold">Variabili dinamiche:</Text>
            {dynamicVariables.map((variable) => (
              <Code key={variable}>{`{{${variable}}}`}</Code>
            ))}
          </Group>
        )}
        {dynamicLinks && dynamicLinks.length > 0 && (
          <Group mb="sm">
            <Text fw="bold">Link dinamici:</Text>
            {dynamicLinks.map((link) => (
              <MarkButton
                key={link.href}
                mark="link"
                icon={<IconLink />}
                label={link.label}
                value={link.href}
              />
            ))}
          </Group>
        )}
        <Editable
          ref={editorRef}
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          autoFocus
          style={{
            border: '1px solid #bbb',
            borderRadius: '0.2rem',
            padding: '0.5rem',
          }}
        />
      </Slate>
      <Button mt="md" onClick={handleSaveClick} loading={isLoading}>
        Salva
      </Button>
    </>
  );
}
